/**
 * AgencyLogo -- the ONE shared primitive for rendering the agency's logo
 * (#3417, D4-WLB-011; follow-up to the expiring-URL fix in #3409).
 *
 * WHY THIS EXISTS
 * ---------------
 * Seven surfaces each hand-rolled `<img src={branding.logo_url} …>` inside a
 * navy chrome slot. An agency logo is very often DARK artwork on a TRANSPARENT
 * background (the reference agency's is), so it drew black-on-navy and read as
 * a broken image on `/login` and in both sidebars. A second defect rode along:
 * the wide 900x240 `letterhead` rendition was being crammed into a 32px square
 * slot while the purpose-built square `logo_compact_url` went unused.
 *
 * THE RULE
 * --------
 * On a surface that can be DARK, the artwork is rendered on a LIGHT TILE, so
 * dark artwork stays legible. This needs no new asset from the agency and is
 * white-label safe in both directions: it works for dark artwork today and does
 * not break a light/reversed logo (which simply sits on white instead of navy).
 * Nothing here is special-cased to any one agency.
 *
 * API
 * ---
 *  - `variant`  "compact"    -> square chrome slot; uses `logo_compact_url`
 *               "letterhead" -> wide lockup;        uses `logo_letterhead_url`
 *               (see `utils/agencyLogo.ts` for the fallback chain)
 *  - `surface`  "dark"     -> always a light tile (navy sidebars, navy auth pages)
 *               "adaptive" -> light tile in dark mode only, via Tailwind `dark:`
 *                             (for theme-adaptive surfaces such as
 *                             `--color-bg-page`, light in light mode, dark in dark)
 *               "light"    -> never a tile (a guaranteed-light surface)
 *  - `size`     "sm" (chrome, 40px tile) | "lg" (auth lockup, 64px tile).
 *               Ignored by the `letterhead` variant, which sizes by height.
 *
 * FALLBACK (white-label safety): with no logo configured we keep each surface's
 * PRE-EXISTING icon treatment -- the accent-tinted `Building2` tile on the auth
 * lockups, a bare accent `Building2` mark in the sidebars -- so an agency that
 * has not uploaded a logo looks exactly as it did before. The agency NAME is
 * rendered by the call site, never here (rendering it twice would duplicate the
 * accessible name next to the sidebar/auth heading).
 *
 * The logo is decorative-adjacent but identity-bearing, so the `<img>` carries
 * the agency name as its `alt`; the icon fallback is `aria-hidden` because the
 * name is always present as adjacent text on every surface that uses it.
 */
import { Building2 } from "lucide-react";
import { useBranding } from "../contexts/branding";
import { agencyLogoSrc } from "../utils/agencyLogo";
import type {
  AgencyLogoSize,
  AgencyLogoSurface,
  AgencyLogoVariant,
} from "../utils/agencyLogo";

export interface AgencyLogoProps {
  variant?: AgencyLogoVariant;
  surface?: AgencyLogoSurface;
  size?: AgencyLogoSize;
  /** Extra classes on the outer tile (margins/positioning only). */
  className?: string;
}

/**
 * The light tile behind the artwork.
 *
 * `--color-logo-tile` (index.css), NOT `bg-white`: Tailwind v4 resolves `white`
 * from `--color-white`, which `.dark` redefines to #1a2332 — so `bg-white`
 * turns DARK in dark mode and the logo vanishes all over again. This token is
 * defined identically in `:root` and `.dark` precisely so the tile cannot
 * follow the theme. A hairline ring keeps its edge visible on a white card.
 */
const TILE_BY_SURFACE: Record<AgencyLogoSurface, string> = {
  dark: "bg-[var(--color-logo-tile)] ring-1 ring-black/5",
  adaptive: "dark:bg-[var(--color-logo-tile)] dark:ring-1 dark:ring-black/5",
  light: "",
};

/** Square chrome-slot geometry: tile box + artwork box. */
const COMPACT_SIZE: Record<AgencyLogoSize, { tile: string; art: string }> = {
  // 40px tile, 28px of artwork -- matches the sidebars' previous h-7 w-7 mark.
  sm: { tile: "h-10 w-10 rounded-lg p-1.5", art: "h-7 w-7" },
  // 64px tile, 32px of artwork -- matches the auth lockups' previous 16/8.
  lg: { tile: "h-16 w-16 rounded-2xl p-4", art: "h-8 w-8" },
};

/** The pre-existing no-logo treatment, preserved per size. */
const FALLBACK_TILE: Record<AgencyLogoSize, string> = {
  // The sidebars had no tile at all -- just the accent mark on navy.
  sm: "",
  // The auth lockups had an accent-tinted rounded tile.
  lg: "rounded-2xl bg-brand-accent/10 border border-brand-accent/20",
};

function cx(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
}

export default function AgencyLogo({
  variant = "compact",
  surface = "adaptive",
  size = "sm",
  className = "",
}: AgencyLogoProps) {
  const branding = useBranding();
  const name = branding.agency_name || "CRM";
  const src = agencyLogoSrc(branding, variant);
  const geometry = COMPACT_SIZE[size];

  if (!src) {
    // No logo configured -> each surface's original icon treatment, untouched.
    return (
      <span
        data-testid="agency-logo-fallback"
        className={cx(
          "inline-flex shrink-0 items-center justify-center",
          variant === "compact" ? geometry.tile : "p-2",
          variant === "compact" ? FALLBACK_TILE[size] : "",
          className,
        )}
      >
        <Building2
          className={cx(
            variant === "compact" ? geometry.art : "h-8 w-8",
            "text-brand-accent",
          )}
          aria-hidden
        />
      </span>
    );
  }

  const tile = TILE_BY_SURFACE[surface];

  return (
    <span
      data-testid="agency-logo"
      className={cx(
        "inline-flex shrink-0 items-center justify-center",
        variant === "compact" ? geometry.tile : "rounded-lg p-2",
        tile,
        className,
      )}
    >
      <img
        src={src}
        alt={name}
        className={cx(
          variant === "compact"
            ? geometry.art
            : "h-12 w-auto max-w-[220px]",
          "object-contain",
        )}
      />
    </span>
  );
}
